// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { createContext, useContext, ReactNode } from 'react'
import { useExchangeRate } from './useExchangeRate'
import { useOtChainState, useOtSelector, OtStoreState } from '@react/redux/store'
import { PoolLytOverviewCTO } from '@shared/PoolLytOverviewCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

interface ExchangeRateContextType {
  exchangeRate: bigint
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

const ExchangeRateContext = createContext<ExchangeRateContextType | null>(null)

export function ExchangeRateProvider({ children }: { children: ReactNode }): React.ReactElement {
  const {
    stageConfiguration: { lytPoolAddr },
  } = useOtChainState()

  const poolLytOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[lytPoolAddr ?? ETH_ADDR_ZERO]
  ) as PoolLytOverviewCTO | undefined

  const { exchangeRate, isLoading, error, refetch } = useExchangeRate({
    initialExchangeRate: BigInt(poolLytOverviewCTO?.exchangeRate ?? 0),
    refetchInterval: 30000, // 30 seconds
  })

  return (
    <ExchangeRateContext.Provider value={{ exchangeRate, isLoading, error, refetch }}>
      {children}
    </ExchangeRateContext.Provider>
  )
}

export function useExchangeRateContext(): ExchangeRateContextType {
  const context = useContext(ExchangeRateContext)
  if (!context) {
    throw new Error('useExchangeRateContext must be used within an ExchangeRateProvider')
  }
  return context
}
